import { useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../api/client'
import type { RuntimeStats, RuntimeView } from '../api/types'
import { useAsync } from '../hooks/useAsync'
import { localeCorrente, t } from '../i18n/lingua'
import { Card, ErrorBanner, Spinner } from './common'
import { DataTable, type Column } from './DataTable'

/**
 * Runtime of the applications from OpenTelemetry (runtime_service): what the
 * services answer, what they call, which service calls which. The CI section
 * and the «Runtime» view of the business service share these tables.
 */

const PERIODI = [1, 24, 24 * 7]

export function SceltaPeriodo({ ore, onOre }: { ore: number; onOre: (n: number) => void }) {
  return (
    <select value={ore} onChange={(e) => onOre(Number(e.target.value))} aria-label={t('Periodo')}>
      {PERIODI.map((h) => (
        <option key={h} value={h}>{h === 1 ? t('Ultima ora') : h === 24 ? t('Ultime 24 ore') : t('Ultimi 7 giorni')}</option>
      ))}
    </select>
  )
}

/** The four number columns every runtime table ends with. */
function colonneNumeri<T extends RuntimeStats>(): Column<T>[] {
  const fmt = (n: number) => n.toLocaleString(localeCorrente(), { maximumFractionDigits: 1 })
  return [
    { key: 'count', header: t('Chiamate'), type: 'number', value: (s) => s.count, render: (s) => fmt(s.count) },
    {
      key: 'errors', header: t('Errori'), type: 'number', value: (s) => (s.errors > 0 ? s.error_rate * 100 : null),
      render: (s) => (s.errors > 0 ? <span className="badge badge--warn">{fmt(s.error_rate * 100)}%</span> : '—'),
    },
    { key: 'avg_ms', header: t('Media ms'), type: 'number', value: (s) => s.avg_ms, render: (s) => fmt(s.avg_ms) },
    {
      key: 'p95_ms',
      header: <span title={t('Il valore più alto fra i 95° percentili di cinque minuti: un limite superiore.')}>{t('p95 ms')}</span>,
      label: t('p95 ms'), type: 'number', value: (s) => s.p95_ms, render: (s) => fmt(s.p95_ms),
    },
  ]
}

export function TabelleRuntime({ dati }: { dati: RuntimeView }) {
  return (
    <>
      {dati.inbound.length > 0 && (
        <>
          <h3 className="guida__titolo">{t('Operazioni ricevute')}</h3>
          <DataTable
            rows={dati.inbound}
            rowKey={(o, i) => `${o.ci_id}|${o.service}|${o.operation}|${o.port}|${i}`}
            columns={[
              { key: 'service', header: t('Servizio') },
              { key: 'operation', header: t('Operazione'), mono: true },
              {
                key: 'port', header: t('Porta, applicazione'), value: (o) => [o.port || '', o.application].filter(Boolean).join(' '),
                render: (o) => <>{o.port ? <span className="mono">{o.port}</span> : '—'}{o.application && <small className="text-muted"> · {o.application}</small>}</>,
              },
              ...colonneNumeri<RuntimeView['inbound'][number]>(),
            ]}
          />
        </>
      )}
      {dati.outbound.length > 0 && (
        <>
          <h3 className="guida__titolo">{t('Chiamate in uscita')}</h3>
          <DataTable
            rows={dati.outbound}
            rowKey={(c, i) => `${c.ci_id}|${c.service}|${c.host}|${c.port}|${c.operation}|${i}`}
            columns={[
              { key: 'service', header: t('Servizio') },
              {
                key: 'target', header: t('Verso'),
                value: (c) => [c.target_ci?.name, c.host, c.port ? `:${c.port}` : '', c.target].filter(Boolean).join(' '),
                render: (c) => (
                  <>
                    {c.target_ci ? <Link to={`/cis/${c.target_ci.id}`}>{c.target_ci.name}</Link> : <span className="mono">{c.host || '—'}</span>}
                    {c.port ? <span className="mono">:{c.port}</span> : null}
                    {c.target_ci && c.host && <small className="text-muted mono"> ({c.host})</small>}
                    {c.target && <small className="text-muted"> · {c.target}</small>}
                  </>
                ),
              },
              { key: 'kind', header: t('Tipo'), value: (c) => [c.protocol, c.system, c.operation].filter(Boolean).join(' · ') },
              ...colonneNumeri<RuntimeView['outbound'][number]>(),
            ]}
          />
        </>
      )}
      {dati.links.length > 0 && (
        <>
          <h3 className="guida__titolo">{t('Servizi che chiamano servizi')}</h3>
          <DataTable
            rows={dati.links}
            rowKey={(l, i) => `${l.ci_id}|${l.from}|${l.to}|${i}`}
            columns={[
              { key: 'from', header: t('Da') },
              { key: 'to', header: t('A') },
              ...colonneNumeri<RuntimeView['links'][number]>(),
            ]}
          />
        </>
      )}
    </>
  )
}

/** The CI page section «Runtime»: CIDetail decides when it exists. */
export function SezioneRuntimeCI({ ciId }: { ciId: string }) {
  const [ore, setOre] = useState(24)
  const dati = useAsync(() => api.ciRuntime(ciId, ore), [ciId, ore])
  return (
    <Card title={t('Runtime (OpenTelemetry)')} actions={<SceltaPeriodo ore={ore} onOre={setOre} />}>
      <p className="field__hint" style={{ marginTop: 0 }}>
        {t('Ciò che le applicazioni di questo server fanno davvero, dalle loro tracce OpenTelemetry raccolte dall’agente CENSO: le operazioni che ricevono, le chiamate che fanno (servizi, database, code) con il CI che le riceve, e quali servizi chiamano quali. Le tracce restano sul server: arrivano solo conteggi, errori e tempi riassunti ogni cinque minuti.')}
      </p>
      <ErrorBanner error={dati.error} />
      {dati.loading && !dati.data ? <Spinner /> : dati.data && (
        dati.data.inbound.length || dati.data.outbound.length || dati.data.links.length
          ? <TabelleRuntime dati={dati.data} />
          : <p className="text-muted">{t('Nessuna traccia nel periodo scelto.')}</p>
      )}
    </Card>
  )
}

/** The «Runtime» view of the business service card. */
export function RuntimeServizio({ serviceId }: { serviceId: string }) {
  const [ore, setOre] = useState(24)
  const dati = useAsync(() => api.serviceRuntime(serviceId, ore), [serviceId, ore])
  return (
    <>
      <div className="toolbar"><SceltaPeriodo ore={ore} onOre={setOre} /></div>
      <ErrorBanner error={dati.error} />
      {dati.loading && !dati.data ? <Spinner /> : dati.data && dati.data.members.length ? (
        dati.data.members.map((m) => (
          <div key={m.ci_id} style={{ marginBottom: 12 }}>
            <h3 style={{ margin: '8px 0' }}>
              <Link to={`/cis/${m.ci_id}`}>{m.ci_name ?? m.ci_id}</Link>{' '}
              <small className="text-muted">{m.ports.length ? `${t('porte')} ${m.ports.join(', ')}` : ''}</small>
            </h3>
            <TabelleRuntime dati={m} />
          </div>
        ))
      ) : (
        <p className="text-muted">
          {t('Nessuna traccia OpenTelemetry dai componenti del servizio: accendi «Runtime» negli agenti CENSO dei server e manda le tracce delle applicazioni a 127.0.0.1:4318.')}
        </p>
      )}
    </>
  )
}
